import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [hoverZoom] - The desktop hover zoom pane.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Recreate the controller when Shopify reconnects this gallery after a variant change.
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('pointermove', this.#handleHoverMove, { signal });
    this.addEventListener('pointerleave', this.#handleHoverLeave, { signal });
    this.addEventListener('click', this.#handleGalleryInteraction, { signal });
    this.addEventListener('keydown', this.#handleGalleryInteraction, { signal });
    window.addEventListener('scroll', this.#handleViewportChange, { passive: true, signal });
    window.addEventListener('resize', this.#handleViewportChange, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    this.#controller.abort();
    super.disconnectedCallback();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.hideHoverZoom();
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleGalleryInteraction = () => {
    this.hideHoverZoom();
  };

  #handleViewportChange = () => {
    this.hideHoverZoom();
  };

  #handleHoverLeave = () => {
    this.hideHoverZoom();
  };

  #handleHoverMove = (event) => {
    const zoomPane = this.refs.hoverZoom;
    if (!zoomPane || !window.matchMedia('(min-width: 1100px) and (hover: hover) and (pointer: fine)').matches) {
      this.hideHoverZoom();
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.product-media-hover-zoom')) return;

    const image = target?.closest('img.product-media__image');
    const mediaContainer = image?.closest('.product-media-container--image');
    if (!(image instanceof HTMLImageElement) || !mediaContainer) {
      this.hideHoverZoom();
      return;
    }

    const imageUrl =
      image.getAttribute('data_max_resolution') ||
      image.getAttribute('data-max-resolution') ||
      image.currentSrc ||
      image.src;
    if (!imageUrl) {
      this.hideHoverZoom();
      return;
    }

    const imageBounds = image.getBoundingClientRect();
    if (imageBounds.width < 80 || imageBounds.height < 80) {
      this.hideHoverZoom();
      return;
    }

    const naturalRatio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
    const zoomWidth = Math.min(640, window.innerWidth * 0.38);
    const zoomHeight = Math.min(760, window.innerHeight * 0.8, zoomWidth / naturalRatio);
    const actualWidth = Math.min(zoomWidth, zoomHeight * naturalRatio);
    const actualHeight = Math.min(zoomHeight, zoomWidth / naturalRatio);
    const gap = 12;
    const edge = 12;
    let left = imageBounds.right + gap;

    if (left + actualWidth > window.innerWidth - edge) {
      left = imageBounds.left - actualWidth - gap;
    }
    left = Math.max(edge, Math.min(left, window.innerWidth - actualWidth - edge));
    const top = Math.max(edge, Math.min(imageBounds.top, window.innerHeight - actualHeight - edge));

    const x = Math.max(0, Math.min(1, (event.clientX - imageBounds.left) / imageBounds.width));
    const y = Math.max(0, Math.min(1, (event.clientY - imageBounds.top) / imageBounds.height));

    zoomPane.style.left = `${left}px`;
    zoomPane.style.top = `${top}px`;
    zoomPane.style.width = `${actualWidth}px`;
    zoomPane.style.height = `${actualHeight}px`;
    zoomPane.style.backgroundImage = `url("${imageUrl.replaceAll('"', '%22')}")`;
    zoomPane.style.backgroundSize = '220% 220%';
    zoomPane.style.backgroundPosition = `${x * 100}% ${y * 100}%`;
    zoomPane.dataset.open = 'true';
  };

  hideHoverZoom() {
    const zoomPane = this.refs.hoverZoom;
    if (zoomPane) delete zoomPane.dataset.open;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.hideHoverZoom();
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
